// src/components/staff/StaffTable.tsx
import React from "react";
import {
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Staff } from "../../api/staff";

type StaffTableProps = {
  staff: Staff[] | null | undefined;
  onEdit: (staff: Staff) => void;
  onDelete?: (staff: Staff) => void;
};

const StaffTable: React.FC<StaffTableProps> = ({ staff, onEdit, onDelete }) => {
  // Ensure staff is always a safe array
  const safeStaff: Staff[] = Array.isArray(staff) ? staff : [];

  return (
    <Card>
      <CardContent>
        {safeStaff.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No staff members configured yet. Click "Add Staff" to create one.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Photo</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Calendar</TableCell>
                <TableCell>Services</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {safeStaff.map((s) => {
                const serviceCount =
                  s.service_ids?.length ?? s.services?.length ?? 0;

                return (
                  <TableRow key={s.staff_id} hover>
                    <TableCell>
                      <Avatar
                        src={s.profile_photo_url || undefined}
                        alt={s.full_name}
                        sx={{ width: 36, height: 36 }}
                      >
                        {s.full_name?.charAt(0) || "?"}
                      </Avatar>
                    </TableCell>

                    <TableCell>{s.full_name}</TableCell>

                    {/* Calendar + status */}
                    <TableCell>
                      {s.calendar_id ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <span>{s.calendar_name || "Assigned calendar"}</span>
                          <Chip
                            label={
                              s.calendar_is_bookable ? "Bookable" : "Disabled"
                            }
                            color={s.calendar_is_bookable ? "success" : "warning"}
                            size="small"
                          />
                        </Box>
                      ) : (
                        "Not assigned"
                      )}
                    </TableCell>

                    <TableCell>
                      {serviceCount > 0 ? `${serviceCount} service(s)` : "None"}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={s.is_active ? "Active" : "Inactive"}
                        color={s.is_active ? "success" : "warning"}
                        size="small"
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEdit(s)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {onDelete && (
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => onDelete(s)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default StaffTable;
